// packages/web/src/components/ClassroomCreator.tsx — misma lógica (cascada, roster, streaming, payload).
// Cambios de presentación: 4 pasos con resumen lateral fijo, errores por campo al intentar avanzar,
// tipo de evaluación y modalidad como opciones visibles, ventana de disponibilidad plegada.
// Los 4 pasos quedan montados (ocultos con `hidden`) para no perder el estado de LessonMaterialUploader.
import {
  DEFAULT_EVALUATION_TYPE,
  EVALUATION_TYPES,
  EVALUATION_TYPE_LABEL,
  EVALUATION_TYPE_PARAMS,
  PIONEROS_EVALUATION_TYPE,
  type EvaluationType,
} from '@marzano/contracts';
import { useEffect, useMemo, useState } from 'react';
import { Check, CheckCircle2, ChevronDown, Loader2, Plus, Search } from 'lucide-react';
import {
  api,
  ApiError,
  type CreateClassroomResponse,
  type PionerosLearningObjective,
  type PionerosLevel,
  type PionerosMilestone,
  type PionerosSubject,
  type RosterLearner,
} from '../api';
import { MODALITY_LABEL, type Modality } from '../types';
import type { LessonMaterialAttach } from '@marzano/contracts';
import { Alert, Button, Card, Copyable, Field, INPUT_CLASS, PageHeader, Select, TABLE } from './ui';
import { LessonMaterialUploader } from './LessonMaterialUploader';

interface Props {
  onClose: () => void;
  onOpenLive?: (classroomId: string) => void;
}

interface StudentRow {
  email: string;
  name: string;
  fromRoster: boolean;
  included: boolean;
}

const MODALITIES: Modality[] = ['written', 'oral', 'mixed', 'simplified_1_3'];
const SITE_ID_PLACEHOLDER = '';
type Step = 1 | 2 | 3 | 4;

export function ClassroomCreator({ onClose, onOpenLive }: Props) {
  // ── Estado y efectos: idénticos a la versión anterior ───────────────────────
  const [name, setName] = useState('');
  const [neeFlag, setNeeFlag] = useState(false);
  const [modality, setModality] = useState<Modality>('written');
  const [levels, setLevels] = useState<PionerosLevel[]>([]);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [levelsError, setLevelsError] = useState<string | null>(null);
  const [selectedLevel, setSelectedLevel] = useState<PionerosLevel | null>(null);
  const [subjects, setSubjects] = useState<PionerosSubject[]>([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState<PionerosSubject | null>(null);
  const [milestones, setMilestones] = useState<PionerosMilestone[]>([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<PionerosMilestone | null>(null);
  const [evaluationType, setEvaluationType] = useState<EvaluationType>(DEFAULT_EVALUATION_TYPE);
  const [selectedOAs, setSelectedOAs] = useState<PionerosLearningObjective[]>([]);
  const primarySelectedOA = selectedOAs[0] ?? null;
  const [roster, setRoster] = useState<StudentRow[]>([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [manualEmail, setManualEmail] = useState('');
  const [manualName, setManualName] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ phase: 'matrix' | 'students'; done: number; total: number; lastEmail: string | null } | null>(null);
  const [result, setResult] = useState<CreateClassroomResponse | null>(null);
  const [lessonMaterial, setLessonMaterial] = useState<LessonMaterialAttach | null>(null);
  const [teacherHint, setTeacherHint] = useState('');
  const [inviteStartsAt, setInviteStartsAt] = useState('');
  const [inviteExpiresAt, setInviteExpiresAt] = useState('');

  // Estado solo de UI.
  const [step, setStep] = useState<Step>(1);
  const [triedStep, setTriedStep] = useState<Record<Step, boolean>>({ 1: false, 2: false, 3: false, 4: false });
  const [rosterQuery, setRosterQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLevelsLoading(true);
    api.listLevels()
      .then(({ levels }) => { if (!cancelled) setLevels(levels); })
      .catch((err: unknown) => { if (!cancelled) setLevelsError(err instanceof ApiError ? err.message : 'No pudimos cargar la lista de niveles del site.'); })
      .finally(() => { if (!cancelled) setLevelsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSelectedSubject(null); setSelectedMilestone(null); setSelectedOAs([]); setSubjects([]); setMilestones([]);
    if (!selectedLevel) { setRoster([]); return; }
    let cancelled = false;
    setSubjectsLoading(true); setRosterLoading(true); setRosterError(null);
    api.listSubjects(SITE_ID_PLACEHOLDER, selectedLevel.name)
      .then(({ subjects }) => { if (!cancelled) setSubjects(subjects); })
      .catch(() => { if (!cancelled) setSubjects([]); })
      .finally(() => { if (!cancelled) setSubjectsLoading(false); });
    api.getClassroomRoster(selectedLevel._id)
      .then((rosterResp) => {
        if (cancelled) return;
        setRoster(rosterResp.learners.map((l: RosterLearner) => ({ email: l.email, name: l.name ?? l.email, fromRoster: true, included: false })));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setRosterError(err instanceof ApiError ? err.message : 'No pudimos cargar la lista de alumnos del nivel.');
        setRoster([]);
      })
      .finally(() => { if (!cancelled) setRosterLoading(false); });
    return () => { cancelled = true; };
  }, [selectedLevel]);

  useEffect(() => {
    setSelectedMilestone(null); setSelectedOAs([]); setMilestones([]);
    if (!selectedLevel || !selectedSubject) return;
    let cancelled = false;
    setMilestonesLoading(true);
    api.listMilestones(SITE_ID_PLACEHOLDER, selectedLevel._id, selectedSubject._id)
      .then(({ milestones }) => { if (!cancelled) setMilestones(milestones); })
      .catch(() => { if (!cancelled) setMilestones([]); })
      .finally(() => { if (!cancelled) setMilestonesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedLevel, selectedSubject]);

  useEffect(() => { setSelectedOAs([]); }, [selectedMilestone]);

  useEffect(() => {
    const fromPioneros = selectedMilestone?.evaluation_type ? PIONEROS_EVALUATION_TYPE[selectedMilestone.evaluation_type] : undefined;
    setEvaluationType(fromPioneros ?? DEFAULT_EVALUATION_TYPE);
  }, [selectedMilestone]);

  const selectedCount = useMemo(() => roster.filter((s) => s.included).length, [roster]);
  const canSubmit = name.trim().length > 0 && selectedLevel !== null && selectedSubject !== null && selectedMilestone !== null && selectedOAs.length > 0 && selectedCount > 0 && !submitting;
  const hitoDescripcionResolved = selectedMilestone?.name ?? '';

  function toggleAll(next: boolean): void { setRoster((rows) => rows.map((r) => ({ ...r, included: next }))); }
  function toggleRow(email: string): void { setRoster((rows) => rows.map((r) => (r.email === email ? { ...r, included: !r.included } : r))); }
  function addManualStudent(): void {
    const email = manualEmail.trim().toLowerCase();
    const nameClean = manualName.trim();
    if (!email || !nameClean) return;
    setRoster((rows) => {
      const idx = rows.findIndex((r) => r.email.toLowerCase() === email);
      if (idx >= 0) { const copy = [...rows]; copy[idx] = { ...copy[idx]!, included: true }; return copy; }
      return [...rows, { email, name: nameClean, fromRoster: false, included: true }];
    });
    setManualEmail(''); setManualName('');
  }

  async function handleSubmit(): Promise<void> {
    if (!canSubmit || !selectedLevel || !selectedSubject || !selectedMilestone || selectedOAs.length === 0 || !primarySelectedOA) {
      setTriedStep({ 1: true, 2: true, 3: true, 4: true });
      return;
    }
    setSubmitting(true); setSubmitError(null);
    setProgress({ phase: 'matrix', done: 0, total: 0, lastEmail: null });
    try {
      const students = roster.filter((r) => r.included).map((r) => ({ email: r.email, name: r.name }));
      const resp = await api.createClassroomStreaming(
        {
          name: name.trim(),
          asignatura: selectedSubject.name,
          nivel_academico: selectedLevel.name,
          oa_codigo: primarySelectedOA.code,
          oa_descripcion: primarySelectedOA.description,
          hito_nombre: selectedMilestone.name,
          hito_descripcion: hitoDescripcionResolved,
          modality,
          nee_flag: neeFlag,
          signature_id: selectedSubject._id,
          milestone_id: selectedMilestone._id,
          evaluation_type: evaluationType,
          students,
          oas: selectedOAs.map((o) => ({ oa_codigo: o.code, oa_descripcion: o.description })),
          ...(lessonMaterial && { lesson_material: lessonMaterial }),
          ...(teacherHint.trim() && { teacher_hint: teacherHint.trim() }),
          ...(inviteStartsAt && { invite_starts_at: new Date(inviteStartsAt).toISOString() }),
          ...(inviteExpiresAt && { invite_expires_at: new Date(inviteExpiresAt).toISOString() }),
        },
        (evt) => {
          if (evt.kind === 'phase') setProgress({ phase: evt.phase, done: 0, total: evt.total, lastEmail: null });
          else if (evt.kind === 'student_created') setProgress({ phase: 'students', done: evt.done, total: evt.total, lastEmail: evt.email });
        },
      );
      setResult(resp);
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false); setProgress(null);
    }
  }

  if (result) return <SuccessView result={result} onClose={onClose} onNew={() => setResult(null)} {...(onOpenLive && { onOpenLive })} />;

  // ── Validación visible por paso (solo presentación) ─────────────────────────
  const errs = {
    name: name.trim() ? null : 'Escribe un nombre para reconocer el aula en tu lista.',
    level: selectedLevel ? null : 'Elige el nivel.',
    subject: selectedSubject ? null : 'Elige la asignatura.',
    milestone: selectedMilestone ? null : 'Elige el hito.',
    oas: selectedOAs.length > 0 ? null : 'Marca al menos un objetivo de aprendizaje.',
    students: selectedCount > 0 ? null : 'Elige al menos un alumno o agrégalo a mano.',
  };
  const stepOk: Record<Step, boolean> = {
    1: !errs.name && !errs.level && !errs.subject && !errs.milestone && !errs.oas,
    2: true,
    3: true,
    4: !errs.students,
  };
  const show = (s: Step, e: string | null) => (triedStep[s] ? e : null);
  const goNext = () => {
    if (!stepOk[step]) { setTriedStep((t) => ({ ...t, [step]: true })); return; }
    if (step < 4) setStep((step + 1) as Step);
  };
  const progressPct = progress && progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : progress?.phase === 'matrix' ? 5 : 0;
  const STEPS: { n: Step; t: string; s: string }[] = [
    { n: 1, t: 'Qué evaluar', s: selectedOAs.length ? `${selectedOAs.map((o) => o.code).join(', ')}` : 'Nombre, nivel, hito y OA' },
    { n: 2, t: 'Actividad', s: `${EVALUATION_TYPE_LABEL[evaluationType]} · ${MODALITY_LABEL[modality]}` },
    { n: 3, t: 'Material y opciones', s: lessonMaterial ? 'Material listo' : 'Opcional' },
    { n: 4, t: 'Alumnos', s: `${selectedCount} elegido${selectedCount === 1 ? '' : 's'}` },
  ];
  const visibleRoster = rosterQuery.trim()
    ? roster.filter((r) => `${r.name} ${r.email}`.toLowerCase().includes(rosterQuery.trim().toLowerCase()))
    : roster;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {submitting && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-live="polite" aria-label="Creando aula">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3"><Loader2 aria-hidden className="w-6 h-6 text-emerald-700 animate-spin" /><h2 className="text-lg font-semibold text-slate-900">Creando aula</h2></div>
            {progress?.phase === 'matrix' ? (
              <p className="text-sm text-slate-700">Generando la matriz curricular para este OA…</p>
            ) : progress ? (
              <>
                <p className="text-sm text-slate-700">Creando links para {progress.total} alumno{progress.total === 1 ? '' : 's'}. No cierres esta ventana.</p>
                <div>
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="text-2xl font-bold text-emerald-800 tabular-nums">{progress.done} <span className="text-slate-500 text-base">de</span> {progress.total}</span>
                    <span className="text-[13px] text-slate-600">{progressPct}%</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-600 transition-all duration-200" style={{ width: `${progressPct}%` }} /></div>
                  {progress.lastEmail && <p className="text-[13px] text-slate-600 mt-2 truncate">Último: {progress.lastEmail}</p>}
                </div>
              </>
            ) : <p className="text-sm text-slate-700">Preparando…</p>}
          </div>
        </div>
      )}

      <PageHeader back={{ label: 'Volver', onClick: onClose }} title="Nueva aula"
        subtitle="Un aula asigna el mismo OA a un grupo de alumnos y genera un link para cada uno." />

      <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)_280px] gap-6 items-start">
        {/* Pasos */}
        <nav aria-label="Pasos" className="lg:sticky lg:top-20">
          <ol className="flex lg:flex-col gap-1 overflow-x-auto">
            {STEPS.map(({ n, t, s }) => {
              const current = n === step;
              const done = n < step && stepOk[n];
              const warn = triedStep[n] && !stepOk[n];
              return (
                <li key={n} className="shrink-0">
                  <button type="button" onClick={() => setStep(n)} aria-current={current ? 'step' : undefined}
                    className={`w-full min-h-[56px] text-left rounded-lg px-2.5 py-2 flex gap-2.5 items-center ${current ? 'bg-marzano-50' : 'hover:bg-slate-100'}`}>
                    <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center text-[13px] font-bold ${current ? 'bg-marzano-600 text-white' : done ? 'bg-emerald-700 text-white' : warn ? 'bg-red-100 text-red-800' : 'bg-slate-200 text-slate-700'}`}>
                      {done ? <Check aria-hidden className="w-4 h-4" /> : warn ? '!' : n}
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-slate-900">{t}</span>
                      <span className={`block text-xs truncate max-w-[150px] ${warn ? 'text-red-700 font-semibold' : 'text-slate-600'}`}>{warn ? 'Falta completar' : s}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        {/* Contenido del paso */}
        <div className="space-y-5 min-w-0">
          <div hidden={step !== 1} className="space-y-5">
            <Card title="1. Qué vas a evaluar">
              <Field label="Nombre del aula" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ej: Ciencias 5°A — hito 2" required error={show(1, errs.name)} />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {levelsError ? <Alert kind="error">{levelsError}</Alert> : (
                  <div>
                    <Select<PionerosLevel> label="Nivel" value={selectedLevel} options={levels} getKey={(l) => l._id}
                      getLabel={(l) => (l.course ? `${l.name} — ${l.course}` : l.name)} onChange={setSelectedLevel}
                      loading={levelsLoading} placeholder="Elige un nivel…" emptyMessage="No hay niveles configurados en este site" />
                    {show(1, errs.level) && <p className="text-[13px] font-medium text-red-700 mt-1.5">{errs.level}</p>}
                  </div>
                )}
                <div>
                  <Select<PionerosSubject> label="Asignatura" value={selectedSubject} options={subjects} getKey={(s) => s._id} getLabel={(s) => s.name}
                    onChange={setSelectedSubject} disabled={!selectedLevel} loading={subjectsLoading}
                    disabledPlaceholder="Elige primero un nivel" emptyMessage="No hay asignaturas para este nivel" />
                  {selectedLevel && show(1, errs.subject) && <p className="text-[13px] font-medium text-red-700 mt-1.5">{errs.subject}</p>}
                </div>
              </div>
              <div>
                <Select<PionerosMilestone> label="Hito" value={selectedMilestone} options={milestones} getKey={(m) => m._id} getLabel={(m) => m.name}
                  onChange={setSelectedMilestone} disabled={!selectedSubject} loading={milestonesLoading}
                  disabledPlaceholder="Elige primero una asignatura" emptyMessage="Sin hitos para (nivel, asignatura)" />
                {selectedSubject && show(1, errs.milestone) && <p className="text-[13px] font-medium text-red-700 mt-1.5">{errs.milestone}</p>}
              </div>

              {selectedMilestone && (
                <fieldset>
                  <div className="flex items-baseline justify-between mb-2">
                    <legend className="text-sm font-medium text-slate-800">
                      Objetivos de aprendizaje del hito <span className="text-red-700" aria-hidden>*</span>{' '}
                      <span className="font-normal text-slate-600">· {selectedOAs.length} de {selectedMilestone.oas.length}</span>
                    </legend>
                    {selectedMilestone.oas.length > 1 && (
                      <button type="button" className="text-[13px] font-semibold text-marzano-600 hover:underline"
                        onClick={() => setSelectedOAs(selectedOAs.length === selectedMilestone.oas.length ? [] : [...selectedMilestone.oas])}>
                        {selectedOAs.length === selectedMilestone.oas.length ? 'Quitar todos' : 'Elegir todos'}
                      </button>
                    )}
                  </div>
                  {selectedMilestone.oas.length === 0 ? (
                    <p className="text-sm text-slate-600 rounded-lg border border-dashed border-slate-300 px-3 py-4 text-center">Este hito no tiene OAs asociados.</p>
                  ) : (
                    <ul className={`max-h-80 overflow-y-auto rounded-lg border divide-y divide-slate-100 ${show(1, errs.oas) ? 'border-red-600' : 'border-slate-200'}`}>
                      {selectedMilestone.oas.map((o) => {
                        const checked = selectedOAs.some((x) => x._id === o._id);
                        return (
                          <li key={o._id}>
                            <label className={`flex items-start gap-3 px-3.5 py-3 cursor-pointer transition-colors ${checked ? 'bg-emerald-50' : 'hover:bg-slate-50'}`}>
                              <input type="checkbox" className="mt-0.5 h-5 w-5 shrink-0 rounded border-slate-400 text-emerald-700 focus:ring-marzano-500" checked={checked}
                                onChange={() => setSelectedOAs((prev) => (checked ? prev.filter((x) => x._id !== o._id) : [...prev, o]))} />
                              <span className="min-w-0 text-sm leading-relaxed">
                                <span className="font-mono text-[13px] font-semibold text-marzano-700 bg-marzano-50 rounded px-1.5 py-0.5 mr-1.5">{o.code}</span>
                                <span className="text-slate-800">{o.description}</span>
                              </span>
                            </label>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {show(1, errs.oas) && <p className="text-[13px] font-medium text-red-700 mt-1.5">{errs.oas}</p>}
                </fieldset>
              )}
              {selectedOAs.length === 1 && hitoDescripcionResolved && (
                <p className="text-[13px] text-slate-700 bg-slate-50 rounded-lg px-3.5 py-3"><span className="font-semibold">Hito:</span> {hitoDescripcionResolved}</p>
              )}
            </Card>
          </div>

          <div hidden={step !== 2} className="space-y-5">
            <Card title="2. Cómo será la actividad">
              <fieldset disabled={!selectedMilestone}>
                <legend className="text-sm font-medium text-slate-800 mb-2">Tipo de evaluación</legend>
                {!selectedMilestone && <p className="text-[13px] text-slate-600 mb-2">Elige primero un hito en el paso 1. El tipo se precarga desde el hito.</p>}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                  {EVALUATION_TYPES.map((t) => {
                    const on = evaluationType === t;
                    return (
                      <label key={t} className={`relative cursor-pointer rounded-lg border-2 px-3.5 py-3 flex flex-col gap-1 ${on ? 'border-marzano-600 bg-marzano-50' : 'border-slate-200 hover:border-slate-300'} has-[:disabled]:opacity-60 has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-amber-500`}>
                        <input type="radio" name="evaluation-type" className="sr-only" checked={on} onChange={() => setEvaluationType(t)} />
                        <span className="text-sm font-semibold text-slate-900">{EVALUATION_TYPE_LABEL[t]}</span>
                        <span className="text-[13px] text-slate-600">{EVALUATION_TYPE_PARAMS[t].expectedMinutesLabel}</span>
                        {on && <Check aria-hidden className="absolute top-2.5 right-2.5 w-4 h-4 text-marzano-600" />}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
              <fieldset>
                <legend className="text-sm font-medium text-slate-800 mb-2">Modalidad</legend>
                <div className="inline-flex flex-wrap rounded-lg border border-slate-200 bg-slate-50 p-1 gap-1">
                  {MODALITIES.map((m) => (
                    <label key={m} className={`cursor-pointer rounded-md px-3.5 py-2 text-sm ${modality === m ? 'bg-white shadow-sm font-semibold text-marzano-700' : 'text-slate-700 hover:text-slate-900'} has-[:focus-visible]:outline has-[:focus-visible]:outline-[3px] has-[:focus-visible]:outline-amber-500`}>
                      <input type="radio" name="modality" className="sr-only" checked={modality === m} onChange={() => setModality(m)} />
                      {MODALITY_LABEL[m]}
                    </label>
                  ))}
                </div>
              </fieldset>
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-3.5 py-3 cursor-pointer">
                <input type="checkbox" role="switch" aria-checked={neeFlag} className="peer sr-only" checked={neeFlag} onChange={(e) => setNeeFlag(e.target.checked)} />
                <span aria-hidden className="mt-0.5 relative h-6 w-10 shrink-0 rounded-full bg-slate-300 transition peer-checked:bg-emerald-700 peer-focus-visible:outline peer-focus-visible:outline-[3px] peer-focus-visible:outline-amber-500 after:absolute after:left-0.5 after:top-0.5 after:h-5 after:w-5 after:rounded-full after:bg-white after:transition peer-checked:after:translate-x-4" />
                <span><span className="block text-sm font-semibold text-slate-900">Aula con adecuaciones (NEE)</span><span className="block text-[13px] text-slate-600">Marca si el grupo requiere adecuaciones curriculares.</span></span>
              </label>
            </Card>
          </div>

          <div hidden={step !== 3} className="space-y-5">
            <LessonMaterialUploader oaCodigo={primarySelectedOA?.code ?? null} oaDescripcion={primarySelectedOA?.description ?? null}
              nivelAcademico={selectedLevel?.name ?? null} asignatura={selectedSubject?.name ?? null} onReady={setLessonMaterial} />
            <Card title="Consideraciones para la IA" description="Opcional. Orienta al entrevistador en cada turno y a la generación de la matriz.">
              <label htmlFor="teacher-hint" className="sr-only">Consideraciones para la IA</label>
              <textarea id="teacher-hint" className={INPUT_CLASS + ' resize-y !mt-0'} rows={3} maxLength={500}
                placeholder="Ej: Haz las preguntas en inglés B1." value={teacherHint} onChange={(e) => setTeacherHint(e.target.value)} />
              <div className="text-xs text-slate-600 text-right tabular-nums -mt-2">{teacherHint.length}/500</div>
            </Card>
            <details className="group rounded-xl border border-slate-200 bg-white">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4">
                <span>
                  <span className="block text-base font-semibold text-slate-900">Disponibilidad del link</span>
                  <span className="block text-sm text-slate-600">
                    {inviteStartsAt || inviteExpiresAt ? 'Ventana personalizada' : 'Por defecto: abre ahora y dura 24 horas'}
                  </span>
                </span>
                <ChevronDown aria-hidden className="w-4 h-4 text-slate-500 transition-transform group-open:rotate-180" />
              </summary>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-5 pb-5">
                <Field label="Empieza" type="datetime-local" value={inviteStartsAt} onChange={(e) => setInviteStartsAt(e.target.value)} hint="Vacío = ahora." />
                <Field label="Vence" type="datetime-local" value={inviteExpiresAt} onChange={(e) => setInviteExpiresAt(e.target.value)} hint="Vacío = 24 horas desde ahora. Máx. 90 días." />
              </div>
            </details>
          </div>

          <div hidden={step !== 4} className="space-y-5">
            <Card title={`4. Alumnos · ${selectedCount} de ${roster.length} elegidos`}
              actions={<>
                <Button variant="secondary" size="sm" onClick={() => toggleAll(true)} disabled={roster.length === 0}>Elegir todos</Button>
                <Button variant="ghost" size="sm" onClick={() => toggleAll(false)} disabled={roster.length === 0}>Ninguno</Button>
              </>}>
              {!selectedLevel && <Alert kind="info">Elige un nivel en el paso 1 para cargar la lista de alumnos.</Alert>}
              {rosterError && <Alert kind="warn">{rosterError} Puedes agregar alumnos a mano abajo.</Alert>}
              {rosterLoading && <p className="flex items-center gap-2 text-sm text-slate-600"><Loader2 aria-hidden className="w-4 h-4 animate-spin" />Cargando alumnos del nivel…</p>}
              {roster.length > 0 && (
                <>
                  <div className="relative">
                    <Search aria-hidden className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                    <label htmlFor="roster-q" className="sr-only">Buscar alumno</label>
                    <input id="roster-q" className={INPUT_CLASS + ' !mt-0 pl-9'} placeholder="Buscar por nombre o email" value={rosterQuery} onChange={(e) => setRosterQuery(e.target.value)} />
                  </div>
                  <div className={`max-h-96 overflow-y-auto rounded-lg border ${show(4, errs.students) ? 'border-red-600' : 'border-slate-200'}`}>
                    <table className={TABLE.table}>
                      <thead className={TABLE.thead + ' sticky top-0'}>
                        <tr><th className="w-10 px-3 py-2"><span className="sr-only">Elegir</span></th><th className={TABLE.th}>Nombre</th><th className={TABLE.th}>Email</th><th className={TABLE.th}>Origen</th></tr>
                      </thead>
                      <tbody>
                        {visibleRoster.map((s) => (
                          <tr key={s.email} className={`border-t border-slate-100 cursor-pointer ${s.included ? 'bg-emerald-50/60' : 'hover:bg-slate-50'}`} onClick={() => toggleRow(s.email)}>
                            <td className="px-3 py-2.5">
                              <input type="checkbox" aria-label={`Elegir a ${s.name}`} className="h-5 w-5 rounded border-slate-400 text-emerald-700 focus:ring-marzano-500"
                                checked={s.included} onChange={() => toggleRow(s.email)} onClick={(e) => e.stopPropagation()} />
                            </td>
                            <td className="px-4 py-2.5 text-slate-900">{s.name}</td>
                            <td className="px-4 py-2.5 text-slate-700">{s.email}</td>
                            <td className="px-4 py-2.5 text-[13px] text-slate-600">{s.fromRoster ? 'Pioneros' : 'Manual'}</td>
                          </tr>
                        ))}
                        {visibleRoster.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-slate-600">Sin resultados para “{rosterQuery}”.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {show(4, errs.students) && <p className="text-[13px] font-medium text-red-700">{errs.students}</p>}
              <div className="rounded-lg border border-dashed border-slate-300 p-3.5">
                <p className="text-sm font-medium text-slate-800 mb-2">Agregar alumno a mano</p>
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2">
                  <input className={INPUT_CLASS + ' !mt-0'} type="email" aria-label="Email" placeholder="Email" value={manualEmail} onChange={(e) => setManualEmail(e.target.value)} />
                  <input className={INPUT_CLASS + ' !mt-0'} aria-label="Nombre" placeholder="Nombre" value={manualName} onChange={(e) => setManualName(e.target.value)} />
                  <Button variant="secondary" onClick={addManualStudent} disabled={!manualEmail || !manualName} leftIcon={<Plus aria-hidden className="w-4 h-4" />}>Agregar</Button>
                </div>
              </div>
            </Card>
          </div>

          {submitError && <Alert kind="error">{submitError}</Alert>}

          <div className="flex justify-between gap-2 border-t border-slate-200 pt-4">
            <Button variant="secondary" onClick={() => (step === 1 ? onClose() : setStep((step - 1) as Step))}>{step === 1 ? 'Cancelar' : '← Anterior'}</Button>
            {step < 4
              ? <Button variant="secondary" className="!bg-marzano-600 !text-white !border-marzano-600 hover:!bg-marzano-700" onClick={goNext}>Continuar →</Button>
              : <Button disabled={submitting} loading={submitting} onClick={() => void handleSubmit()}>Crear aula · {selectedCount} alumno{selectedCount !== 1 ? 's' : ''}</Button>}
          </div>
        </div>

        {/* Resumen */}
        <aside aria-label="Resumen" className="lg:sticky lg:top-20 rounded-xl border border-slate-200 bg-white p-4 space-y-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-600">Resumen</h2>
          <dl className="space-y-2 text-sm">
            <SummaryRow label="Nombre" value={name.trim() || null} />
            <SummaryRow label="Nivel" value={selectedLevel ? (selectedLevel.course ? `${selectedLevel.name} — ${selectedLevel.course}` : selectedLevel.name) : null} />
            <SummaryRow label="Asignatura" value={selectedSubject?.name ?? null} />
            <SummaryRow label="OA" value={selectedOAs.length ? selectedOAs.map((o) => o.code).join(', ') : null} />
            <SummaryRow label="Actividad" value={`${EVALUATION_TYPE_LABEL[evaluationType]} · ${EVALUATION_TYPE_PARAMS[evaluationType].expectedMinutesLabel}`} />
            <SummaryRow label="Modalidad" value={MODALITY_LABEL[modality] + (neeFlag ? ' · NEE' : '')} />
            <SummaryRow label="Material" value={lessonMaterial ? 'Listo' : 'Sin material'} optional />
            <SummaryRow label="Link" value={inviteStartsAt || inviteExpiresAt ? 'Ventana personalizada' : '24 horas'} optional />
            <SummaryRow label="Alumnos" value={selectedCount > 0 ? String(selectedCount) : null} />
          </dl>
          <Button className="w-full" disabled={!canSubmit} loading={submitting} onClick={() => void handleSubmit()}>
            Crear aula · {selectedCount} alumno{selectedCount !== 1 ? 's' : ''}
          </Button>
          {!canSubmit && !submitting && <p className="text-[13px] text-slate-600">Completa lo marcado como “Falta” para crear el aula.</p>}
        </aside>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, optional }: { label: string; value: string | null; optional?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-600 shrink-0">{label}</dt>
      <dd className={`text-right truncate ${value ? 'text-slate-900' : optional ? 'text-slate-500' : 'text-red-700 font-semibold'}`} title={value ?? undefined}>{value ?? 'Falta'}</dd>
    </div>
  );
}

function SuccessView({ result, onClose, onNew, onOpenLive }: {
  result: CreateClassroomResponse; onClose: () => void; onNew: () => void; onOpenLive?: (classroomId: string) => void;
}) {
  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <PageHeader
        eyebrow={<span className="inline-flex items-center gap-1.5 text-emerald-800 font-semibold"><CheckCircle2 aria-hidden className="w-4 h-4" />Aula creada</span>}
        title={result.name}
        actions={<>
          {onOpenLive && <Button onClick={() => onOpenLive(result.classroom_id)}>Ver progreso en vivo</Button>}
          <Button variant="secondary" onClick={onNew}>Crear otra aula</Button>
        </>}
      />
      <Alert kind="success">
        Los links se envían automáticamente al correo de cada alumno. Si no llegan en unos minutos, cópialos desde aquí y compártelos. Cada link es único y vence en 24 horas.
      </Alert>
      <div className={TABLE.wrap}>
        <table className={TABLE.table}>
          <thead className={TABLE.thead}><tr><th className={TABLE.th}>Alumno</th><th className={TABLE.th}>Email</th><th className={TABLE.th}>Link</th></tr></thead>
          <tbody>
            {result.students.map((s) => (
              <tr key={s.pilot_id} className={TABLE.tr}>
                <td className={TABLE.td + ' font-semibold text-slate-900'}>{s.name}</td>
                <td className={TABLE.td + ' text-slate-700'}>{s.email}</td>
                <td className={TABLE.td + ' max-w-[320px]'}><Copyable value={s.invite_link} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button variant="ghost" onClick={onClose}>← Volver</Button>
    </div>
  );
}
